import { z } from 'zod';

/**
 * Moving content out of the legacy group workspaces into the workspace of an account and application.
 *
 * An admin picks, for each object at the top of a legacy workspace, the application it belongs to. The move itself is
 * done by chat-api from a queue; what is asked for, and how it went, is recorded and listed here.
 */

/** Where a request is: queued, then processing, then done, or failed (which may be asked for again). */
export const LEGACY_MOVE_STATUSES = ['queued', 'processing', 'done', 'failed'] as const;
export const LegacyMoveStatusSchema = z.enum(LEGACY_MOVE_STATUSES);
export type LegacyMoveStatus = z.infer<typeof LegacyMoveStatusSchema>;

/**
 * Why a request to move an object was not taken. `enqueue-failed` is no longer given: a request whose job could not be
 * published is taken, and its job is published again later. It stays so that clients that know the reason still parse.
 */
export const LEGACY_MOVE_REJECTIONS = ['not-movable', 'application-not-held', 'move-in-progress', 'enqueue-failed'] as const;
export const LegacyMoveRejectionSchema = z.enum(LEGACY_MOVE_REJECTIONS);
export type LegacyMoveRejection = z.infer<typeof LegacyMoveRejectionSchema>;

const PageQuery = {
  take: z.coerce.number().int().min(1).max(200).default(50),
  skip: z.coerce.number().int().min(0).default(0),
};

const ApplicationSchema = z.object({
  /** In the one form it is compared in: a uuid with hyphens, or a lower-case slug. */
  identifier: z.string(),
  name: z.string(),
});

/** The latest request for an object, when there is one. */
const MoveSummarySchema = z.object({
  identifier: z.uuid(),
  status: LegacyMoveStatusSchema,
  targetApplicationIdentifier: z.string(),
  requestedOn: z.coerce.date(),
  /** A message only. */
  error: z.string().nullable(),
});

export namespace ListLegacyAccounts {
  export const Schema = {
    Response: z.object({
      accounts: z.array(
        z.object({
          identifier: z.uuid(),
          name: z.string(),
          /** How many top-level objects are still in its group workspace. */
          objects: z.number(),
          /** Of those, how many have a request queued or being carried out. */
          inProgress: z.number(),
          /** The applications the account holds: where its objects can be moved to. */
          applications: z.array(ApplicationSchema),
        })
      ),
    }),
  };

  export type Response = z.infer<typeof Schema.Response>;
}

export namespace ListLegacyObjects {
  export const Schema = {
    Query: z.object({
      accountIdentifier: z.uuid().optional(),
      /** Part of the name of the object, ignoring case. */
      search: z.string().trim().min(1).max(200).optional(),
      objectTypeId: z.coerce.number().int().optional(),
      ...PageQuery,
    }),
    Response: z.object({
      items: z.array(
        z.object({
          objectIdentifier: z.uuid(),
          name: z.string(),
          objectTypeId: z.number(),
          accountIdentifier: z.uuid(),
          /** Null for content that belongs to the system and not to a person. */
          ownerName: z.string().nullable(),
          systemOwned: z.boolean(),
          /** How many live objects are below it: they move with it. */
          descendants: z.number(),
          /** Someone is excluded from the access it would inherit, and will not be given it in the new workspace. */
          hasExclusion: z.boolean(),
          move: MoveSummarySchema.nullable(),
        })
      ),
      /** Of everything the filters match, for paging. */
      total: z.number(),
      /** The accounts of the items, with the applications each holds: where an object of it can be moved to. */
      accounts: z.record(
        z.string(),
        z.object({
          name: z.string(),
          applications: z.array(ApplicationSchema),
        })
      ),
    }),
  };

  export type Query = z.infer<typeof Schema.Query>;
  export type Response = z.infer<typeof Schema.Response>;
}

/** The most objects one request may ask to move. */
export const MAX_LEGACY_MOVES_PER_REQUEST = 100;

export namespace RequestLegacyMoves {
  export const Schema = {
    Body: z.object({
      moves: z
        .array(
          z.object({
            objectIdentifier: z.uuid(),
            /** A uuid in either form, or a slug, as listed for the account of the object. */
            applicationIdentifier: z.string().trim().min(1).max(200),
          })
        )
        .min(1)
        .max(MAX_LEGACY_MOVES_PER_REQUEST),
    }),
    Response: z.object({
      results: z.array(
        z.discriminatedUnion('outcome', [
          z.object({ objectIdentifier: z.uuid(), outcome: z.literal('queued'), moveIdentifier: z.uuid() }),
          z.object({ objectIdentifier: z.uuid(), outcome: z.literal('rejected'), reason: LegacyMoveRejectionSchema }),
        ])
      ),
    }),
  };

  export type Body = z.infer<typeof Schema.Body>;
  export type Response = z.infer<typeof Schema.Response>;
}

export namespace ListLegacyMoves {
  export const Schema = {
    Query: z.object({
      accountIdentifier: z.uuid().optional(),
      status: LegacyMoveStatusSchema.optional(),
      ...PageQuery,
    }),
    Response: z.object({
      items: z.array(
        z.object({
          identifier: z.uuid(),
          objectIdentifier: z.uuid(),
          objectName: z.string(),
          accountIdentifier: z.uuid(),
          accountName: z.string(),
          targetApplicationIdentifier: z.string(),
          targetApplicationName: z.string().nullable(),
          status: LegacyMoveStatusSchema,
          requestedByUserIdentifier: z.uuid(),
          requestedOn: z.coerce.date(),
          completedOn: z.coerce.date().nullable(),
          /** Set once the object has moved. */
          objectsMoved: z.number().nullable(),
          usersWithAccessBefore: z.number().nullable(),
          /** How many people were given the object in its new workspace. */
          grantedCount: z.number().nullable(),
          /** How many could not be, because they do not hold the application. */
          droppedCount: z.number().nullable(),
          /** A message only. */
          error: z.string().nullable(),
        })
      ),
      total: z.number(),
    }),
  };

  export type Query = z.infer<typeof Schema.Query>;
  export type Response = z.infer<typeof Schema.Response>;
}
